import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { createRun, getRun, listRuns } from './runsAdminApi'
import {
  EvalRunCreateInput,
  RunFilters,
  runRefetchInterval,
} from './runsAdminModel'

const ADMIN_LIST_KEY = ['runsAdmin', 'list'] as const
const ADMIN_DETAIL_KEY = ['runsAdmin', 'detail'] as const

/**
 * Run detail for the monitor view. Polls while the run is active and stops
 * once it reaches a terminal status (#96). Disabled until a run is selected.
 */
export const useRunQuery = (runId: string | null) => {
  return useQuery({
    queryKey: [...ADMIN_DETAIL_KEY, runId],
    queryFn: () => getRun(runId as string),
    enabled: runId != null,
    retry: false,
    refetchInterval: (data) => runRefetchInterval(data?.status),
    onError: () => {
      notification.error({ message: 'Failed to load run' })
    },
  })
}

export const useRunsListQuery = (filters: RunFilters) => {
  return useQuery({
    queryKey: [...ADMIN_LIST_KEY, filters],
    queryFn: () => listRuns(filters),
    keepPreviousData: true,
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load runs' })
    },
  })
}

export const useCreateRunMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: EvalRunCreateInput) => createRun(input), {
    onSuccess: (accepted) => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({
        message: 'Run started',
        description: `Queued ${accepted.planned_trials} trial(s).`,
      })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to start run', description })
    },
  })
}
